import { Badge, Button, Card, Pagination, Tabs, Tooltip } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import './OrderIssuesPopover.scss';
import { formatDate } from './HistoryLogs';
import TabPane from 'antd/es/tabs/TabPane';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { doGetIssues, doGetIssuesForOrder, doMarkAsSeen, doMarkAllAsSeen } from '../../modules/issues/stores/thunk';
import {
  selectActiveIssues,
  selectActiveIssuesPagination,
  selectOrderActiveIssuesPagination,
  selectOrderResolvedIssues,
  selectOrderResolvedIssuesPagination,
  selectResolvedIssues,
  selectResolvedIssuesPagination,
  updateActiveIssuesPagination,
  updateResolvedIssuesPagination,
  updateOrderActiveIssuesPagination,
  updateOrderResolvedIssuesPagination,
  selectOrderActiveIssues,
} from '../../modules/issues/stores/slice';
import { CheckCircleOutlined } from '@ant-design/icons';

import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const OrderIssues = ({ orderId, setPopoverIssuesVisible }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  //const userRole = useSelector((state) => state.auth.user.role);

  // Determine the selectors and fetch logic based on the presence of `orderId`
  const activeIssues = useSelector((state) => (orderId ? selectOrderActiveIssues(state) : selectActiveIssues(state)));
  const resolvedIssues = useSelector((state) =>
    orderId ? selectOrderResolvedIssues(state) : selectResolvedIssues(state),
  );

  const activeIssuesPagination = useSelector((state) =>
    orderId ? selectOrderActiveIssuesPagination(state) : selectActiveIssuesPagination(state),
  );

  const resolvedIssuesPagination = useSelector((state) =>
    orderId ? selectOrderResolvedIssuesPagination(state) : selectResolvedIssuesPagination(state),
  );

  const hasUnseenActiveIssues = activeIssues?.some((issue) => !issue.hasSeen);
  const hasUnseenResolvedIssues = resolvedIssues?.some((issue) => !issue.hasSeen);

  const [activeTab, setActiveTab] = useState('1');

  const fetchIssues = useCallback(
    async (status, page) => {
      if (orderId) {
        doGetIssuesForOrder({
          status,
          orderId,
          page,
          take: 10,
        });
      } else {
        await dispatch(
          doGetIssues({
            status,
            page,
            take: 10,
          }),
        );
      }
    },
    [dispatch, orderId],
  );

  useEffect(() => {
    // Initial fetch
    fetchIssues('active');
    fetchIssues('resolved');
  }, [fetchIssues]);

  const handleActivePageChange = (page) => {
    if (orderId) {
      // Update pagination for order-specific active issues and fetch
      dispatch(updateOrderActiveIssuesPagination({ currentPage: page }));
      fetchIssues('active', page);
    } else {
      // Update pagination for all active issues and fetch
      dispatch(updateActiveIssuesPagination({ currentPage: page }));
      fetchIssues('active', page);
    }
  };

  const handleResolvedPageChange = (page) => {
    // Update resolved issues pagination and fetch
    if (orderId) {
      dispatch(updateOrderResolvedIssuesPagination({ currentPage: page }));
      fetchIssues('resolved', page);
    } else {
      dispatch(updateResolvedIssuesPagination({ currentPage: page }));
      fetchIssues('resolved', page);
    }
  };

  if ((!activeIssues || activeIssues.length === 0) && (!resolvedIssues || resolvedIssues.length === 0)) {
    return (
      <div className="issuepopover">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ fontSize: '3em', marginBottom: '0px' }}>🤩</h2>
          <p>No Issues!</p>
        </div>
      </div>
    );
  }

  const handleMarkSeen = async (issueId) => {
    await dispatch(doMarkAsSeen({ issueId: issueId }));
  };

  const handleMarkAllSeen = async () => {
    await dispatch(doMarkAllAsSeen({}));
  };

  const onClickIssue = async (issue) => {
    setPopoverIssuesVisible(false);
    history.push(`/issues?issueId=${issue.issueId}`);
  };

  // useEffect(() => {
  //   if (setPopoverIssuesVisible) {
  //     // Refetch issues whenever the popover is opened
  //     fetchIssues('active', activeIssuesPagination.currentPage || 1);
  //     fetchIssues('resolved', resolvedIssuesPagination.currentPage || 1);
  //   }
  // }, [setPopoverIssuesVisible, fetchIssues, activeIssuesPagination.currentPage, resolvedIssuesPagination.currentPage]);

  return (
    <div className="issuepopover">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Issues</h3>
        {(hasUnseenActiveIssues || hasUnseenResolvedIssues) && !orderId && (
          <Tooltip title="Mark all as Seen">
            <Button
              type="text"
              shape="circle"
              icon={<CheckCircleOutlined />}
              onClick={handleMarkAllSeen}
              style={{
                color: '#1890ff',
                marginLeft: 'auto', // Align to the right
                marginRight: 0,
                background: 'none', // No background on hover
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'none')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            />
          </Tooltip>
        )}
      </div>
      <Tabs defaultActiveKey="1" activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            hasUnseenActiveIssues ? (
              <Badge dot>{`Active (${activeIssuesPagination.totalItems})`}</Badge>
            ) : (
              `Active (${activeIssuesPagination.totalItems})`
            )
          }
          key="1"
        >
          {activeIssues
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .map((issue) => (
              <IssueCard
                issue={issue}
                key={issue.issueId}
                openIssue={onClickIssue}
                //userRole={userRole}
                markSeen={handleMarkSeen}
              />
            ))}
          <Pagination
            current={activeIssuesPagination.currentPage}
            pageSize={activeIssuesPagination.pageSize}
            total={activeIssuesPagination.totalItems}
            onChange={handleActivePageChange}
            showSizeChanger={false}
            style={{
              marginTop: '16px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          />
        </TabPane>
        <TabPane
          tab={
            hasUnseenResolvedIssues ? (
              <Badge dot>{`Resolved (${resolvedIssuesPagination.totalItems})`}</Badge>
            ) : (
              `Resolved (${resolvedIssuesPagination.totalItems})`
            )
          }
          key="2"
        >
          {resolvedIssues
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .map((issue) => (
              <IssueCard
                issue={issue}
                key={issue.issueId}
                openIssue={onClickIssue}
                //userRole={userRole}
                markSeen={handleMarkSeen}
              />
            ))}
          <Pagination
            current={resolvedIssuesPagination.currentPage}
            pageSize={resolvedIssuesPagination.pageSize}
            total={resolvedIssuesPagination.totalItems}
            onChange={handleResolvedPageChange}
            showSizeChanger={false}
            style={{
              marginTop: '16px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default OrderIssues;

// Redesigned IssueCard Component
const IssueCard = ({ issue, openIssue, markSeen }) => {
  return (
    <Card className={`issuecard ${!issue.hasSeen ? 'unseen' : ''}`} onClick={() => openIssue(issue)}>
      <div className="header">
        <p className="issue-date">{formatDate(issue.createdAt)}</p>
        <Tooltip title="Issue Id">
          {' '}
          <h4 className="issue-id">{issue?.issueId}</h4>{' '}
        </Tooltip>
      </div>

      <div className="company-info">
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <div className="logo">
            <img src={issue?.companyLogoThumbUrl} alt="Company Logo" />
          </div>
          <span className="company-name" title={issue?.companyName}>
            {issue?.companyName}
          </span>
        </div>
        {!issue.hasSeen && (
          <Tooltip title="Mark as Seen">
            <Button
              type="text"
              shape="circle"
              icon={<CheckCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                markSeen(issue.id);
              }}
              className="mark-seen-button"
              style={{ marginLeft: 'auto', background: 'none' }}
            />
          </Tooltip>
        )}
      </div>

      {/* <div className="details">
        <div className="info">
          {userRole === ROLES.ADMIN && <p className="priority">{formatPriority(issue?.priority)}</p>}
        </div>
        <span className={`status ${issue.status?.toLowerCase()}`}>{issue.status?.split('_').join(' ')}</span>
      </div> */}

      <div className="issue-content">
        <p className="reason">{formatIssueReason(issue.reason)}</p>
        <p className="message" title={issue.message}>
          {issue.comments?.length > 0 ? issue.comments[issue.comments.length - 1].message : issue.message}
        </p>
      </div>
    </Card>
  );
};

export const formatIssueReason = (reason) => {
  return reason
    ?.split('-')
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
};
